import type { flashcardDifficultyEnum, SubmissionKind } from '@/db/schema/enums'
import { commentBodyResolver } from '@/lib/coaching/review-comment-body'
import {
  isClientVisibleCommentStatus,
  isSavableComment,
} from '@/lib/coaching/review-comment-visibility'
import { isTickedCard } from '@/lib/coaching/submission-kind'
import type { KindCounts, TypeCounts } from '../../store'
import type {
  PostMortemApproach,
  PostMortemApproachSource,
  PostMortemData,
  PostMortemDifficulty,
  PracticeSessionRow,
  RankingChip,
  RankingChipTone,
  ReviewComment,
  ReviewComplexity,
  ReviewComplexityClaim,
  ReviewPlanWeek,
  ReviewRanking,
  ReviewStatus,
  ReviewSubmission,
  ReviewType,
  ReviewVerification,
  ReviewWeekStatus,
  SubmittedCode,
  SubmittedReviewRow,
  VerificationFailedTest,
  VerificationSubmit,
} from '../contracts'

/**
 * Pure mappers from the `coachingV2.reviews.*` wire shapes onto the Review
 * tab's contracts (LG-503). Kept free of React and tRPC so the translation —
 * which is where the honesty rules live — is unit-testable on its own.
 *
 * The rule everywhere below: render what the queue returns and nothing else.
 * Fields the shipped read surface has no source for (the sprint a submission
 * belongs to, why the ranker ranked it where it did, company pacing bands)
 * come back empty rather than invented, and the components skip them.
 */

/**
 * tRPC serializes `Date` to an ISO string over the wire (no transformer is
 * configured — see `lib/trpc.ts`), while the inferred output types still say
 * `Date`. Accept both rather than trusting either.
 */
export type WireDate = Date | string

/**
 * `ReviewPostMortemDto` as the browser receives it — the queue row's live
 * post-mortem. `updatedAt` is an ISO string on both sides: the DTO declares it
 * as one precisely because no tRPC date transformer would have converted it
 * back. The rating is the raw `FlashcardDifficulty` member; labelling it is
 * this module's job.
 */
export interface WirePostMortem {
  difficulty: string | null
  answers: { questionId: string; question: string | null; answer: string }[]
  /**
   * The approach the client took, already resolved server-side — which column
   * won, whose words the label is, and blanks stripped. `null` when the
   * write-up records none. The tag is `string`, not the union, for the same
   * reason `difficulty` is: this is what the server sent, and it can be a
   * deploy ahead of this bundle.
   *
   * Optional as well as nullable, because the skew runs the other way too: an
   * API instance a deploy *behind* the loaded bundle sends a `postMortem` with
   * no `approach` key at all. Declaring it required would be the seam telling
   * a lie the guard below already disbelieves — and would hide the field being
   * renamed server-side, which typechecks fine and reads `undefined` forever.
   */
  approach?: { source: string; label: string } | null
  timeComplexity: WireComplexityClaim | null
  spaceComplexity: WireComplexityClaim | null
  updatedAt: string
}

/** `ReviewComplexityClaimDto` as the browser receives it (LG-639). */
export interface WireComplexityClaim {
  value: string | null
  explanation: string | null
}

export interface QueueItem {
  reviewId: string
  reviewStatus: string
  submissionNumber: number
  kind: string
  submittedAt: WireDate
  durationSeconds: number
  artifactSnapshot: Record<string, unknown>
  rankingSnapshot: Record<string, unknown>
  postMortem: WirePostMortem | null
  coachTitle: string
  reviewedAt: WireDate | null
  /** When the client did the work — what the tab files the row under. */
  workedAt: WireDate
  /** Whether the work was unassigned when it began — the server's judgment. */
  unassigned: boolean
  /** Whether a coach pulled this work into the week after the fact (LG-647). */
  pulledIn: boolean
  /**
   * The engagement's verification targets (spec D18), live rather than
   * snapshotted. Null on both for every engagement today.
   */
  targetRuns: number | null
  targetSubmits: number | null
}

/**
 * `PracticeSessionItemDto` as the browser receives it — a session with no
 * submission behind it (LG-646): the `Worked · not submitted` row, and every
 * unassigned-practice row.
 */
export interface PracticeItem {
  sessionId: string
  title: string | null
  workedAt: WireDate
  durationSeconds: number | null
  runCount: number
  submitCount: number
  passed: boolean
  unassigned: boolean
  /** Whether the server would accept a pull of this session (LG-647). */
  pullable: boolean
  postMortem: WirePostMortem | null
}

/**
 * `ReviewWeekDto` as the browser receives it — one row of the tab's spine.
 * `status` is `string`, not the union, for the same reason a queue row's is:
 * this is what the server sent, and it can be a deploy ahead of this bundle.
 */
export interface WireWeek {
  id: string
  weekNumber: number
  startsOnKey: string
  endsOnKey: string
  status: string
  awaitingCount: number
  workedCount: number
}

export interface CommentRow {
  id: string
  source: string
  kind: string
  status: string
  sectionKey: string
  targetLabel: string
  body: string
  currentRevisionId: string | null
  lineStart: number | null
  lineEnd: number | null
  diffBefore: string | null
  diffAfter: string | null
}

export interface RevisionRow {
  id: string
  commentId: string
  revisionNumber: number
  status: string
  revisionSource: string
  body: string
}

const MINUTE_MS = 60_000
const HOUR_MS = 60 * MINUTE_MS
const DAY_MS = 24 * HOUR_MS

export function toDate(value: WireDate): Date {
  return value instanceof Date ? value : new Date(value)
}

/**
 * The error a query is worth surfacing: none while its own cached data is on
 * screen. After a failed *background* refetch React Query keeps the last good
 * answer, and an error banner over data the coach is reading is a worse lie
 * than the silent retry. Paired per query, and only per query — one query's
 * cached data must never silence a *different* query's failure, or a hard
 * failure hides behind an unrelated success and reads as loading forever.
 */
export function surfacedError(query: {
  data: unknown
  error: unknown
}): unknown {
  return query.data === undefined ? (query.error ?? null) : null
}

/** `mm:ss`, or `h:mm:ss` past an hour — the format the TOTAL tile expects. */
export function formatDurationSeconds(totalSeconds: number): string {
  const safe = Number.isFinite(totalSeconds) ? Math.max(0, totalSeconds) : 0
  const seconds = Math.round(safe) % 60
  const minutes = Math.floor(Math.round(safe) / 60) % 60
  const hours = Math.floor(Math.round(safe) / 3600)
  const pad = (value: number): string => String(value).padStart(2, '0')
  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(seconds)}`
    : `${minutes}:${pad(seconds)}`
}

/** "3 days ago" / "2 hours ago" / "just now" — never a fabricated precision. */
export function formatRelativeWhen(value: WireDate, now: Date): string {
  const at = toDate(value)
  if (Number.isNaN(at.getTime())) return 'at an unknown time'
  const elapsed = now.getTime() - at.getTime()
  if (elapsed < MINUTE_MS) return 'just now'
  const units: [ms: number, label: string][] = [
    [DAY_MS, 'day'],
    [HOUR_MS, 'hour'],
    [MINUTE_MS, 'minute'],
  ]
  for (const [ms, label] of units) {
    const count = Math.floor(elapsed / ms)
    if (count >= 1) return `${count} ${label}${count === 1 ? '' : 's'} ago`
  }
  return 'just now'
}

// `Partial`, so the fallback below is a checked branch rather than decoration
// — `noUncheckedIndexedAccess` is off, and a plain `Record<string, …>` would
// claim every key is present.
// Annotated on `string` so the `?? 'coding'` fallback below stays a checked
// branch for a kind this bundle has never heard of, and `satisfies` on the
// literal so the enum still has to be covered: a fifth kind is a compile error
// here rather than a row silently pilled and bodied as coding.
const TYPE_BY_SUBMISSION_KIND: Readonly<
  Record<string, ReviewType | undefined>
> = {
  coding: 'coding',
  system_design: 'sd',
  behavioral: 'behavioral',
  manual: 'task',
} satisfies Record<SubmissionKind, ReviewType>

export function submissionType(kind: string): ReviewType {
  // Unknown kinds render as coding rather than crashing the queue; the type
  // pill is cosmetic and the comment/lifecycle wiring does not depend on it.
  // `SubmissionKind` is a closed enum (db/schema/enums.ts) and every arm of
  // the tab — artifact schemas, `BODY_BY_TYPE`, the type pill — is written
  // against exactly its members, so a fifth kind is a UI ticket, not a silent
  // path.
  //
  // `Object.hasOwn` for the reason `difficultyLabel` gives below: a bare `??`
  // never fires for `'__proto__'` or `'constructor'`, which resolve up the
  // prototype chain and would hand an *object* back typed as a `ReviewType` —
  // straight into `BODY_BY_TYPE` and the type counters.
  if (!Object.hasOwn(TYPE_BY_SUBMISSION_KIND, kind)) return 'coding'
  return TYPE_BY_SUBMISSION_KIND[kind] ?? 'coding'
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function asNonblankString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null
}

function asFiniteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

/**
 * `11/12` — passed of total, off the artifact's `executionSummary`. null when
 * the snapshot stores no summary (a recorded state: the client's work
 * qualified without a run), or stores one this cannot read. The collapsed
 * row's chip and the Code section's summary line both derive from this single
 * reader, so the two can never disagree about the same submission.
 */
function passedRatio(artifact: Record<string, unknown>): string | null {
  const summary = asRecord(artifact.executionSummary)
  const passed = summary ? asFiniteNumber(summary.passed) : null
  const failed = summary ? asFiniteNumber(summary.failed) : null
  if (passed === null || failed === null || passed + failed <= 0) return null
  return `${passed}/${passed + failed}`
}

/**
 * The coding artifact's chips: language, and the execution summary's pass
 * count. Read defensively — `artifactSnapshot` is untyped jsonb on the wire,
 * and every schema version is stored as-is. These two fields happen to be
 * spelled the same in v1 and v2, so the chips need no version dispatch; the
 * post-mortem below does.
 */
export function artifactChips(item: {
  kind: string
  submissionNumber: number
  artifactSnapshot: unknown
}): string[] {
  const chips: string[] = []
  const artifact = asRecord(item.artifactSnapshot) ?? {}
  if (item.kind === 'coding') {
    const language = asNonblankString(artifact.language)
    if (language !== null) chips.push(language)
    const ratio = passedRatio(artifact)
    if (ratio !== null) chips.push(`${ratio} tests passed`)
  }
  // A ticked card's `submissionNumber` counts un-ticks, not attempts, and
  // "Attempt 3" on a chapter reading says something untrue about the client.
  if (item.submissionNumber > 1 && !isTickedCard(item.kind)) {
    chips.push(`Attempt ${item.submissionNumber}`)
  }
  return chips
}

/**
 * The client's submitted code off the artifact snapshot (LG-517) — what the
 * Code section renders instead of the LIS seed it shipped with. null when the
 * snapshot holds no readable `finalCode`, which is every non-coding artifact
 * and any coding one written before the field existed; the section then
 * renders its empty state rather than someone else's code.
 *
 * Like the chips above, this needs no version dispatch: `language` and
 * `finalCode` are spelled the same in coding v1 and v2.
 *
 * The line array is the anchor space for per-line comments: line N is
 * `lines[N - 1]`, and `ReviewComment.lines` indexes the same way, so this is
 * the one place the split is defined.
 */
export function artifactCode(artifactSnapshot: unknown): SubmittedCode | null {
  const artifact = asRecord(artifactSnapshot)
  if (artifact === null) return null
  const finalCode = asNonblankString(artifact.finalCode)
  if (finalCode === null) return null
  const ratio = passedRatio(artifact)
  return {
    language: asNonblankString(artifact.language),
    // One trailing newline is a file-ending convention, not a blank last line
    // the client wrote — stripping exactly one keeps the last line number on
    // the last line of code, while a deliberate blank line before it stays.
    // CRLF-tolerant: an editor that stored Windows endings would otherwise
    // leave a stray \r on the end of every line, label and listing alike.
    lines: finalCode.replace(/\r?\n$/, '').split(/\r?\n/),
    testSummary: ratio === null ? null : `${ratio} passed`,
  }
}

/** A recorded count: a nonnegative whole number, or nothing readable. */
function asCount(value: unknown): number | null {
  const count = asFiniteNumber(value)
  return count === null || count < 0 || !Number.isInteger(count) ? null : count
}

/**
 * The one `ExecutionStatus` under which an attempt's counters are a finished
 * verdict — the artifact-side mirror of `isPassingSubmit`'s own check in
 * `lib/coaching/coding-submission-eligibility.ts`. A `running` attempt's
 * counters are a snapshot mid-flight and a `failed` one's describe the attempt
 * that broke; neither is a solved problem, whatever the numbers say.
 */
const SUCCESSFUL_TERMINAL_STATUS = 'completed'

/**
 * A graded attempt that finished and passed every case. Everything else is a
 * failure the coach should see — including an attempt that errored out with no
 * cases recorded at all, which reads as 0 of 0 and must not pass for a clean
 * run.
 */
function submitPassed(submit: {
  status: string
  passed: number
  total: number
}): boolean {
  return (
    submit.status === SUCCESSFUL_TERMINAL_STATUS &&
    submit.total > 0 &&
    submit.passed === submit.total
  )
}

/**
 * The cases one attempt got wrong. An element this cannot read is dropped
 * rather than rendered blank: the attempt still appears in the list either
 * way, so the failure is never hidden — only its detail is, which is the same
 * state as an attempt whose structured results were never stored.
 */
function artifactFailedTests(value: unknown): VerificationFailedTest[] {
  if (!Array.isArray(value)) return []
  return value.flatMap((entry) => {
    const record = asRecord(entry)
    if (record === null) return []
    // `asCount`, not just "an integer": the write schema constrains `index`
    // only to int, so a negative one is storable, and rendering it would label
    // a case ("Test 0") that exists in neither the artifact nor the editor.
    const index = asCount(record.index)
    if (index === null) return []
    return [{ index, reason: asNonblankString(record.reason) }]
  })
}

/** One `submits[]` entry, plus the instant it is ordered by. */
interface ReadSubmit extends Omit<VerificationSubmit, 'attempt'> {
  status: string
  /**
   * `at` in epoch milliseconds, or `Number.POSITIVE_INFINITY` when the stored
   * string is not a readable date — which sorts it last rather than poisoning
   * every comparison it takes part in.
   */
  atMs: number
}

function readSubmit(value: unknown): ReadSubmit | null {
  const record = asRecord(value)
  if (record === null) return null
  const executionId = asNonblankString(record.executionId)
  // No id, no identity: an entry this cannot tell apart from its neighbours
  // cannot be numbered as a distinct attempt. Schema-guaranteed, so this is
  // the unreachable branch rather than an expected shape.
  if (executionId === null) return null
  const atMs = Date.parse(String(record.at))
  // Counters zero as a pair, mirroring the producer's `gradedCounts`: a
  // half-recorded or incoherent (`passed > total`) attempt is an unrecorded
  // one, and 0 of 0 is a failure rather than a pass — never "5/0 passed".
  const passed = asCount(record.passed)
  const total = asCount(record.total)
  const graded =
    passed !== null && total !== null && passed <= total
      ? { passed, total }
      : { passed: 0, total: 0 }
  return {
    executionId,
    atMs: Number.isNaN(atMs) ? Number.POSITIVE_INFINITY : atMs,
    status: asNonblankString(record.status) ?? '',
    ...graded,
    failedTests: artifactFailedTests(record.failedTests),
  }
}

/**
 * Every graded attempt that did not pass, numbered by when it happened.
 *
 * Ordered here rather than trusted from the stored array: position in jsonb is
 * not a fact, and `at` + `executionId` is the same key the producer sorted the
 * executions by (`byFinishedAtThenId`), so this reproduces its order instead
 * of inventing a second one. Attempt numbers count every readable submit, not
 * only the failing ones — "Submit #3 failed" has to mean the client's third
 * submit.
 */
function artifactFailedSubmits(value: unknown): VerificationSubmit[] {
  if (!Array.isArray(value)) return []
  const submits = value.flatMap((entry) => readSubmit(entry) ?? [])
  submits.sort(
    (left, right) =>
      left.atMs - right.atMs ||
      left.executionId.localeCompare(right.executionId),
  )
  return submits.flatMap((submit, index) =>
    submitPassed(submit)
      ? []
      : [
          {
            executionId: submit.executionId,
            attempt: index + 1,
            passed: submit.passed,
            total: submit.total,
            failedTests: submit.failedTests,
          },
        ],
  )
}

/**
 * What the client actually did (LG-637, spec T3): the artifact's own run and
 * submit counts, the engagement's live targets, and every graded attempt that
 * did not pass.
 *
 * null when the snapshot holds no readable `counts` — every non-coding
 * artifact, and every coding one written before `codingArtifactV2` existed.
 * The section is then absent rather than reporting zero runs and zero submits,
 * which is a different and false claim about the client.
 *
 * Targets arrive already read off the engagement and are normalized the same
 * way the counts are, so a stored value that is not a whole count reads as no
 * target — no target line and no verdict pill — rather than judging the client
 * against it.
 */
export function artifactVerification(
  artifactSnapshot: unknown,
  targets: { runs: number | null; submits: number | null },
): ReviewVerification | null {
  const artifact = asRecord(artifactSnapshot)
  if (artifact === null) return null
  const counts = asRecord(artifact.counts)
  if (counts === null) return null
  const runs = asCount(counts.runs)
  const submits = asCount(counts.submits)
  if (runs === null || submits === null) return null
  return {
    runs: { value: runs, target: asCount(targets.runs) },
    submits: { value: submits, target: asCount(targets.submits) },
    failedSubmits: artifactFailedSubmits(artifact.submits),
  }
}

/**
 * The client's self-assessment, under the label the editor's own post-mortem
 * offered it by (`components/editor/postmortem-section.tsx`) minus its emoji.
 * A rating the coach reads as "Hard" must be the one the client picked as
 * "Hard"; translating the stored member into some second vocabulary would put
 * words in their mouth.
 */
const DIFFICULTY_LABELS = {
  again: 'Again',
  struggled: 'Hard',
  intermediate_effort: 'Good',
  effortless: 'Easy',
  // `satisfies`, not a plain object: keyed over `FlashcardDifficulty` itself,
  // so adding a fifth member to the pg enum is a compile error here instead of
  // the coach's rating pill silently vanishing — which reads identically to a
  // client who skipped the rating. The runtime `?? null` below still covers a
  // wire value from a newer server than this bundle.
} satisfies Record<FlashcardDifficulty, PostMortemDifficulty>

type FlashcardDifficulty = (typeof flashcardDifficultyEnum.enumValues)[number]

/**
 * A wire rating this build has a label for, or `null`. Guarded rather than
 * indexed blind, so a value from a newer server than this bundle drops the
 * pill instead of rendering `undefined`.
 */
function difficultyLabel(value: string | null): PostMortemDifficulty | null {
  // `Object.hasOwn`, not `in`: `in` walks the prototype chain, so `'toString'`
  // would pass the guard and hand back a *function* typed as a label — which
  // the section then indexes `DIFFICULTY_META` with and throws on.
  if (value === null || !Object.hasOwn(DIFFICULTY_LABELS, value)) return null
  return DIFFICULTY_LABELS[value as FlashcardDifficulty]
}

/**
 * Every approach source this bundle knows about, keyed over the union rather
 * than listed beside it: `Record` demands each member, so a source added to
 * the server DTO is a compile error here — and one at the section's renderer
 * map — instead of a tile that quietly stops appearing. Same shape, and the
 * same reason, as `DIFFICULTY_LABELS` above.
 */
const APPROACH_SOURCES: Record<PostMortemApproachSource, true> = {
  catalog: true,
  client: true,
}

function isApproachSource(value: string): value is PostMortemApproachSource {
  // `Object.hasOwn`, not `in`: `in` walks the prototype chain, so `'toString'`
  // would otherwise pass the guard.
  return Object.hasOwn(APPROACH_SOURCES, value)
}

/**
 * The approach as this build can show it, or `null`.
 *
 * Which of the two stored columns won is the read's decision — it depends on
 * the reported approach id, which never leaves the server — so nothing is
 * re-derived here. What is checked is the wire: a missing field, a blank
 * label, or a `source` this bundle has no renderer for all mean no tile.
 * That is the rollout window, where the API is a deploy ahead of the loaded
 * bundle: the alternatives are a review pane that throws, or a label of
 * unknown provenance rendered as though the client typed it.
 */
function liveApproach(
  approach: WirePostMortem['approach'],
): PostMortemApproach | null {
  if (!approach) return null
  const label = asNonblankString(approach.label)
  if (label === null || !isApproachSource(approach.source)) return null
  return { source: approach.source, label }
}

/**
 * The queue row's live post-mortem → the section's model.
 *
 * Read off the client's session at request time, never out of the artifact
 * (LG-765): the artifact carries execution facts only, and the write-up has
 * its own lifetime — it can be written, or rewritten, after the submission
 * exists. A member of `FlashcardDifficulty` this build has no label for is
 * dropped to `null` rather than shown raw; the answers still render.
 */
export function livePostMortem(
  postMortem: WirePostMortem | null | undefined,
  now: Date,
): PostMortemData | null {
  if (!postMortem) return null
  const answers = postMortem.answers.flatMap((entry) =>
    asNonblankString(entry.answer) === null
      ? []
      : [
          {
            id: entry.questionId,
            question: entry.question,
            answer: entry.answer,
          },
        ],
  )
  const difficulty = difficultyLabel(postMortem.difficulty)
  const approach = liveApproach(postMortem.approach)
  // A reported approach counts as a write-up on its own, the same way a rating
  // does: it is a thing the client recorded, and dropping the row would render
  // no section for a post-mortem that has something for the coach to read.
  if (answers.length === 0 && difficulty === null && approach === null) {
    return null
  }
  return {
    difficulty,
    answers,
    approach,
    lastEditedWhen: formatRelativeWhen(postMortem.updatedAt, now),
  }
}

/**
 * The queue row's complexity claim → the Complexity section's model.
 *
 * A separate mapper from `livePostMortem`, and a separate section, because the
 * two have different absence rules: a client who recorded only their big-O has
 * no post-mortem write-up worth a card but does have a complexity claim worth
 * a comment.
 *
 * Blank halves are re-dropped to `null` here even though `complexityClaim`
 * already did it server-side. Not belt-and-braces for its own sake: this is
 * the wire boundary, the value arrives as whatever the server sent, and an `''`
 * reaching an atom is the empty box the ticket forbids.
 */
export function liveComplexity(
  postMortem: WirePostMortem | null | undefined,
): ReviewComplexity | null {
  if (!postMortem) return null
  const time = liveComplexityClaim(postMortem.timeComplexity)
  const space = liveComplexityClaim(postMortem.spaceComplexity)
  if (time === null && space === null) return null
  return { time, space }
}

function liveComplexityClaim(
  claim: WireComplexityClaim | null | undefined,
): ReviewComplexityClaim | null {
  if (!claim) return null
  const value = asNonblankString(claim.value)
  const explanation = asNonblankString(claim.explanation)
  if (value === null && explanation === null) return null
  return { value, explanation }
}

const RANKING_CHIP_TONES: readonly RankingChipTone[] = ['amber', 'red', 'blue']

const isRankingChipTone = (value: unknown): value is RankingChipTone =>
  typeof value === 'string' &&
  (RANKING_CHIP_TONES as readonly string[]).includes(value)

/**
 * The snapshot's `signals` — the evidence chips under the reason. A signal is
 * carried only when it says all four things the chip shows: a partial one
 * would render as an unlabelled pill or an empty tooltip, which reads as a
 * claim with nothing behind it.
 */
function rankingChips(value: unknown): RankingChip[] {
  if (!Array.isArray(value)) return []
  return value.flatMap((entry) => {
    const signal = asRecord(entry)
    if (signal === null) return []
    const icon = asNonblankString(signal.icon)
    const label = asNonblankString(signal.label)
    const detail = asNonblankString(signal.detail)
    if (icon === null || label === null || detail === null) return []
    if (!isRankingChipTone(signal.tone)) return []
    return [{ icon, label, tone: signal.tone, detail }]
  })
}

/**
 * The stored ranking snapshot → the banner's ranking, or `null` when the
 * snapshot holds no judgement to render (LG-643, spec D16).
 *
 * `rankingSnapshot` is untyped jsonb, so this reads it the same defensive way
 * `artifactChips` reads the artifact. A ranking exists only when the snapshot
 * carries both a queue position and the reason for it:
 *
 *   { schemaVersion, position: 3, reason: '…', signals: [{ icon, label, tone, detail }] }
 *
 * Every review written today carries `submitForReview`'s placeholder —
 * `{ schemaVersion: 1, basis: 'submission_created' }`, which records that a
 * submission happened and nothing about rank — and falls through to `null`.
 * The day a ranker writes the shape above, the banner appears with what it
 * wrote and no frontend changes.
 */
export function snapshotRanking(
  rankingSnapshot: Record<string, unknown>,
): ReviewRanking | null {
  const snapshot = asRecord(rankingSnapshot)
  if (snapshot === null) return null
  const position = asFiniteNumber(snapshot.position)
  const reason = asNonblankString(snapshot.reason)
  if (position === null || !Number.isInteger(position) || position < 1) {
    return null
  }
  if (reason === null) return null
  return { position, reason, chips: rankingChips(snapshot.signals) }
}

const REVIEW_STATUSES: ReviewStatus[] = [
  'awaiting',
  'in_review',
  'reviewed',
  'reopened',
  'superseded',
]

// Keyed over the union rather than listed beside it, like `APPROACH_SOURCES`
// above: `satisfies Record<ReviewWeekStatus, true>` makes a status added to
// the pg enum a build error here, instead of one that quietly falls through
// the narrowing below and reaches the header pill labelled "Draft".
const REVIEW_WEEK_STATUSES = {
  draft: true,
  published: true,
  closed: true,
} satisfies Record<ReviewWeekStatus, true>

/**
 * A week status this bundle knows, or `draft` for one it does not.
 *
 * `draft` is the safe unknown: it is the state that promises the client
 * nothing, so a status added server-side reads as "not released" until the UI
 * learns about it, rather than as a week the client has already been given.
 */
export function reviewWeekStatus(value: string): ReviewWeekStatus {
  return Object.hasOwn(REVIEW_WEEK_STATUSES, value)
    ? (value as ReviewWeekStatus)
    : 'draft'
}

function reviewStatus(value: string): ReviewStatus | undefined {
  return REVIEW_STATUSES.find((status) => status === value)
}

/**
 * One queue row → one `ReviewSubmission`. `ranking` is whatever the row's own
 * ranking snapshot holds, and is absent while that is the placeholder — the
 * row's index is its position in the week it was worked in, ordered by when
 * the client worked it, not a rank anybody decided, so it is never shown as
 * one (spec D16). It was a priority ordering before LG-646 flipped the tab's
 * spine to the plan week; it is chronology now, and neither is a rank.
 */
export function toReviewSubmission(
  item: QueueItem,
  comments: ReviewComment[],
  now: Date,
): SubmittedReviewRow {
  const code = artifactCode(item.artifactSnapshot)
  const ranking = snapshotRanking(item.rankingSnapshot)
  return {
    id: item.reviewId,
    type: submissionType(item.kind),
    title: item.coachTitle,
    chips: artifactChips(item),
    submittedWhen: `${
      isTickedCard(item.kind) ? 'Marked done' : 'Submitted'
    } ${formatRelativeWhen(item.submittedAt, now)}`,
    // Spread for the same reason as `code` below: absent, not undefined, is
    // what `ranking?:` means — nothing has ranked this submission.
    ...(ranking === null ? {} : { ranking }),
    pacing: {
      total: formatDurationSeconds(item.durationSeconds),
      totalNote: '',
      companyRows: [],
    },
    // Spread, not `code: … ?? undefined`: the key is absent when the artifact
    // carries no code, which is what `code?:` means to every reader.
    ...(code === null ? {} : { code }),
    workedAtMs: toDate(item.workedAt).getTime(),
    postMortem: livePostMortem(item.postMortem, now),
    complexity: liveComplexity(item.postMortem),
    verification: artifactVerification(item.artifactSnapshot, {
      runs: item.targetRuns,
      submits: item.targetSubmits,
    }),
    comments,
    reviewStatus: reviewStatus(item.reviewStatus),
    // Spread like `ranking`: present-or-absent is the contract, and absent
    // means assigned — the default assumption; the badge marks the exceptions.
    ...(item.unassigned ? { unassigned: true as const } : {}),
    ...(item.pulledIn ? { pulledIn: true as const } : {}),
  }
}

/**
 * One practice-session row → the same `ReviewSubmission` shape every other
 * row renders through (LG-646): a session the client worked and never
 * submitted, so there is no review to hang lifecycle actions or comments on —
 * `notSubmitted` is what the shells key the read-only treatment off.
 *
 * The chips say only what the session can prove: the tracked duration when
 * one was measured, the run and submit counts always, and a pass verdict only
 * where a submit existed to be judged.
 */
export function toPracticeRow(
  item: PracticeItem,
  now: Date,
): PracticeSessionRow {
  const counted = (count: number, noun: string): string =>
    `${count} ${noun}${count === 1 ? '' : 's'}`
  const chips: string[] = []
  if (item.durationSeconds !== null) {
    chips.push(formatDurationSeconds(item.durationSeconds))
  }
  chips.push(counted(item.runCount, 'run'), counted(item.submitCount, 'submit'))
  if (item.submitCount > 0) chips.push(item.passed ? 'Passed' : 'Not passed')

  return {
    id: item.sessionId,
    type: 'coding',
    // A session outlives its problem; the row must not render a blank name.
    title: item.title ?? 'Removed problem',
    chips,
    submittedWhen: `Worked ${formatRelativeWhen(item.workedAt, now)}`,
    workedAtMs: toDate(item.workedAt).getTime(),
    pacing: {
      total:
        item.durationSeconds === null
          ? ''
          : formatDurationSeconds(item.durationSeconds),
      totalNote: '',
      companyRows: [],
    },
    postMortem: livePostMortem(item.postMortem, now),
    complexity: liveComplexity(item.postMortem),
    comments: [],
    notSubmitted: true,
    ...(item.unassigned ? { unassigned: true as const } : {}),
    // The server's judgment, like `unassigned` above: whether the eligibility
    // module would turn this session into a review. Re-deriving it from the
    // row's counters here would put spec D2's rule in two places and offer the
    // coach a button whose only answer is a refusal the moment they drift.
    ...(item.pullable ? { pullable: true as const } : {}),
  }
}

/**
 * A week's rows in the order the client worked them, oldest first (ADR 0013):
 * a week's list is the story of what they did, so it reads forwards even
 * though the weeks themselves read backwards.
 *
 * The review id settles ties, so two sessions recorded at the same instant
 * never swap places between renders.
 */
export function sortByWorkedAt(
  submissions: readonly ReviewSubmission[],
): ReviewSubmission[] {
  return [...submissions].sort(
    (left, right) =>
      left.workedAtMs - right.workedAtMs || left.id.localeCompare(right.id),
  )
}

/** One wire plan week → the section contract. */
export function toReviewPlanWeek(week: WireWeek): ReviewPlanWeek {
  return {
    id: week.id,
    number: week.weekNumber,
    startsOnKey: week.startsOnKey,
    endsOnKey: week.endsOnKey,
    status: reviewWeekStatus(week.status),
    awaitingCount: week.awaitingCount,
    workedCount: week.workedCount,
  }
}

// Annotated with the contract's own unions, and narrowing through a predicate
// rather than an assertion: drop or rename a member of either union and this
// list stops compiling, instead of manufacturing a value outside it that blows
// up in `KIND_ACCENT[comment.kind]` at render time.
const COMMENT_KINDS: readonly ReviewComment['kind'][] = [
  'praise',
  'critique',
  'suggestion',
]
const COMMENT_STATUSES: readonly ReviewComment['status'][] = [
  'pending',
  'approved',
  'posted',
  'dismissed',
]

const isCommentKind = (value: string): value is ReviewComment['kind'] =>
  (COMMENT_KINDS as readonly string[]).includes(value)

const isCommentStatus = (value: string): value is ReviewComment['status'] =>
  (COMMENT_STATUSES as readonly string[]).includes(value)

/**
 * `alt` — the canned rewrite the card's "Rewrite…" panel drops into the body —
 * is the newest unpublished `ai_rewrite` revision of that comment, if the AI
 * has drafted one. A published revision is already the comment body.
 */
function altBody(
  commentId: string,
  revisions: RevisionRow[],
): string | undefined {
  let best: RevisionRow | undefined
  for (const revision of revisions) {
    if (revision.commentId !== commentId) continue
    if (
      revision.status !== 'draft' ||
      revision.revisionSource !== 'ai_rewrite'
    ) {
      continue
    }
    if (!best || revision.revisionNumber > best.revisionNumber) best = revision
  }
  return best?.body
}

/**
 * Comment rows → the tab's comment model.
 *
 * The body is the comment's *current* text, resolved through the published
 * revision `currentRevisionId` points at (`commentBodyResolver`, the rule the
 * client's feedback reader shares): `editComment` never rewrites
 * `ReviewComment.body`, so `row.body` alone would show the coach the text a
 * published edit already replaced (LG-529).
 *
 * `drafts` layers the coach's unsaved textarea edits over that. They stay
 * local until the coach saves one, which writes a `coach_edit` revision and
 * publishes it (`saveEdit`, which owns when a draft is dropped). `edited`
 * marks a body the coach can act on: text that differs
 * from the stored wording on a comment the service will accept an edit for —
 * `isSavableComment`, the same predicate the service checks — so no card ever
 * offers to save an edit that would come back PRECONDITION_FAILED.
 *
 * `submissionKind` is the owning submission's raw wire kind (the same value
 * `artifactChips` branches on). Pending AI rows on a *coding* submission are
 * not surfaced (LG-632, spec D15): the ticket strips coding's AI-draft
 * surfaces, so a stored `pending` coding row stays hidden rather than
 * re-opening them; it stays out of the counters either way
 * (`isClientVisibleCommentStatus`). Every other kind keeps its full pending
 * lifecycle — the card's approve/rewrite/dismiss actions resolve it.
 */
export function toReviewComments(
  rows: CommentRow[],
  revisions: RevisionRow[],
  drafts: Readonly<Partial<Record<string, string>>>,
  submissionKind: string,
): ReviewComment[] {
  const currentBody = commentBodyResolver(revisions)
  return rows.flatMap((row) => {
    if (!isCommentKind(row.kind) || !isCommentStatus(row.status)) return []
    if (row.source !== 'ai' && row.source !== 'coach') return []
    if (
      submissionKind === 'coding' &&
      row.source === 'ai' &&
      row.status === 'pending'
    ) {
      return []
    }
    const stored = currentBody(row)
    const draft = drafts[row.id]
    const comment: ReviewComment = {
      id: row.id,
      kind: row.kind,
      // Verbatim: every anchor the composer ever offered mounts again —
      // LG-632's re-anchor map floated `complexity` and `verification` rows to
      // `code` while their sections were cut, and retired with LG-639 and
      // LG-637 bringing those sections back. A stored key with no data behind
      // it still renders: each revived section mounts for its comments alone.
      section: row.sectionKey,
      target: row.targetLabel,
      source: row.source,
      status: row.status,
      body: draft ?? stored,
      // Typed-over text the coach can still save. Identical text is not an
      // edit (no no-op revision), and a pending AI draft cannot be saved at
      // all until it is approved or dismissed — offering it would be a button
      // the service refuses.
      edited:
        draft !== undefined &&
        draft !== stored &&
        isSavableComment({ source: row.source, status: row.status }),
    }
    const alt = altBody(row.id, revisions)
    if (alt !== undefined) comment.alt = alt
    if (row.diffBefore !== null && row.diffAfter !== null) {
      comment.diff = { before: row.diffBefore, after: row.diffAfter }
    }
    if (row.lineStart !== null && row.lineEnd !== null) {
      comment.lines = [row.lineStart, row.lineEnd]
    }
    return [comment]
  })
}

// The counters count the comments that reached the client (§8.8). The rule is
// imported as `isClientVisibleCommentStatus` — the same leaf definition
// `clientVisibleComments` and the client's feedback reader apply — rather
// than kept in step by comment.

export function emptyKindCounts(): KindCounts {
  return { praise: 0, critique: 0, suggestion: 0 }
}

export function emptyTypeCounts(): TypeCounts {
  return { coding: 0, sd: 0, behavioral: 0, task: 0 }
}

export function countByKind(comments: ReviewComment[]): KindCounts {
  const counts = emptyKindCounts()
  for (const comment of comments) {
    if (isClientVisibleCommentStatus(comment.status)) counts[comment.kind] += 1
  }
  return counts
}

export function countByQuestionType(
  submissions: ReviewSubmission[],
): TypeCounts {
  const counts = emptyTypeCounts()
  for (const submission of submissions) {
    for (const comment of submission.comments) {
      if (isClientVisibleCommentStatus(comment.status)) {
        counts[submission.type] += 1
      }
    }
  }
  return counts
}

/**
 * A comment target key for coach-authored comments. The composer only knows
 * the human label it is anchored to ('Code · L5–L8'), while the server wants a
 * stable machine key alongside it; deriving one from the section + label keeps
 * two comments on the same atom sharing a key.
 *
 * Complexity has two spellings in the wild, and this is where one of them
 * comes from. Rows the composer wrote before LG-632 carry the derived
 * `toTargetKey('complexity', 'Complexity · Time')` — `complexity:complexity-time`
 * — while the revived atoms post the explicit `complexity:time`. Both render on
 * the revived section, and nothing reads `targetKey` back today, so this costs
 * nothing yet; whoever builds per-atom grouping should expect both.
 */
export function toTargetKey(section: string, target: string): string {
  const slug = target
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return slug.length > 0 ? `${section}:${slug}` : section
}
